import {Column} from "@t/agenda/Columns.ts";
import type {ComputedRef, Ref, VNodeChild} from "vue";
import {ObjectAccessFlag} from "@t/security/ObjectAccess.ts";

export type FormatterType =
	/** Use for debugging only. Prints input parameters and delegate the work to the sub-formatter */
	"DUMP" |
	/** String output */
	"STRING" |
	/** Data/datetime formatting */
	"DATE" |
	/** Boolean column - renders to icon */
	"BOOLEAN" |
	/** Numeric value formatter */
	"NUMBER" |
	/** Currency aware money formatting */
	"MONEY" |
	/** Percent value formatting |  e.g. "80 %" */
	"PERCENT" |
	/** Progressbar formatting, e.g. [=====     ] */
	"BAR" |
	/** Formats number as a byte size |  e.g. "16 kB" */
	"BYTE_SIZE" |
	/** Static content */
	"CONST" |
	/** Join multiple fragments together |  Supports: prefix |  suffix and custom separator */
	"JOIN" |
	/** Generates HTML icon tag */
	"ICON" |
	/** Generates img element */
	"IMAGE" |
	/** Select source data using indices */
	"INDEX" |
	/** Map arguments to another value |  e.g. enums to icon names */
	"MAP" |
	/** Map arguments to corresponding list item value */
	"LIST" |
	/** Transforms argmuments using given text template */
	"ARG" |
	/** Condition */
	"IF" |
	/** Remove N data objects from argument list */
	"POP" |
	/** Use static arguments for sub-formatter */
	"WITH" |
	/** Renders user */
	"USER" |
	/** Render action link */
	"ACTION" |
	/** Render content using column's default formatter */
	"DEFAULT" |
	/** Render Stage tag (colorized pill) using list metadata */
	"TAG" |
	/** Render duration |  e.g. "3h 16m" */
	"DURATION" |
	/** Rearranging data arguments to the sub-formatter (supports property expansion) */
	"PACK" |
	/** Format enum implementing Fragmentable interface */
	"ENUM" |
	/** Resource bundle lookup */
	"I18N" |
	/** Expand collections and arrays into multiple arguments for sub-formatter */
	"EXPAND" |
	/** Concatenate output of multiple sub-formatters */
	"CONCAT" |
	/** Wrap content in a hyperlink, first data argument is the URL */
	"LINK" |
	/** Wrap sub-formatter output in an HTML element */
	"WRAP";

export type SlotFactory = () => VNodeChild[];

export type ActionLinkRenderFunction = (content:SlotFactory, action:string, srcType:number, params:Record<string, any>) => SlotFactory;

export interface CellContext {
	/** Rendered column */
	column: Column;
	/** Primary key of rendered record */
	primaryKey: string;

	itemKey: number|string;
	index: number;

	object: object;
	actionLink: ActionLinkRenderFunction;
}

export interface CellFormatterData {
	type: FormatterType,
}

export interface DumpFormatterData extends CellFormatterData {
	type: "DUMP";
	format: CellFormatterData;
}

export interface StringFormatterData extends CellFormatterData {
	type: "STRING";
	/** Input data is safe HTML */
	html?: boolean;
	/** Limit output to the specified number of characters */
	limit?: number;
}

export interface DateFormatterData extends CellFormatterData {
	type: "DATE";
	pattern?: string;
}

export interface BooleanFormatterData extends CellFormatterData {
	type: "BOOLEAN";
	trueIcon?: string;
	falseIcon?: string;
}

export interface NumberFormatterData extends CellFormatterData {
	type: "NUMBER";
}

export interface MoneyFormatterData extends CellFormatterData {
	type: "MONEY";
	symbol?: boolean;
}

export interface PercentFormatterData extends CellFormatterData {
	type: "PERCENT";
	divider?: number;
}

export interface BarFormatterData extends CellFormatterData {
	type: "BAR";
	width: number;
	denominator?: number;
}

export interface ByteSizeFormatterData extends CellFormatterData {
	type: "BYTE_SIZE";
}

export interface ConstFormatterData extends CellFormatterData {
	type: "CONST";
	text: string;
	html?: boolean;
}

export interface JoinFormatterData extends CellFormatterData {
	type: "JOIN";
	input: CellFormatterData;
	separator?: CellFormatterData;
	prefix?: CellFormatterData;
	suffix?: CellFormatterData;
	ifEmpty?: CellFormatterData;
}

export interface IconFormatterData extends CellFormatterData {
	type: "ICON";
}

export interface ImageFormatterData extends CellFormatterData {
	type: "IMAGE";
	maxWidth?: number;
	maxHeight?: number;
}

export interface IndexFormatterData extends CellFormatterData {
	type: "INDEX";
	indices?: number[];
	format: CellFormatterData;
}

export interface MapFormatterData extends CellFormatterData {
	type: "MAP";
	format: CellFormatterData;
	map: Record<string, any>;
	defaultValue?: any;
}

export interface ListFormatterData extends CellFormatterData {
	type: "LIST";
	listName?: string;
}

export interface ArgFormatterData extends CellFormatterData {
	type: "ARG";
	format: CellFormatterData;
	template: string;
}

export type ConditionOp = "AND" | "OR" | "NOT" | "EQ" | "LT" | "GT" | "LTE" | "GTE" | "TRUTHFUL" | "ACCESS";

export interface ConditionData {
	op: ConditionOp;
	/** UnaryCondition (TRUTHFUL) - index into data array */
	index?: number;
	/** CompareCondition - left index / And,Or - left sub-condition */
	left?: number | ConditionData;
	/** CompareCondition - right index or static value / And,Or - right sub-condition */
	right?: any;
	/** Negate - nested condition */
	test?: ConditionData;
	/** Test access flag of the row object */
	flag?: ObjectAccessFlag;
}

export interface IfFormatterData extends CellFormatterData {
	type: "IF";
	trueFormat: CellFormatterData;
	falseFormat?: CellFormatterData;
	test?: ConditionData;
}

export interface PopFormatterData extends CellFormatterData {
	type: "POP";
	format: CellFormatterData;
	count?: number;
}

export interface WithFormatterData extends CellFormatterData {
	type: "WITH";
	format: CellFormatterData;
	data: any[];
}

export interface UserFormatterData extends CellFormatterData {
	type: "USER";
}

export interface ActionParam {
	name: string;
	source: "ARG" | "OBJECT" | "STATIC";
	index?: number;
	path?: string;
	value?: any;
}

export interface ActionFormatterData extends CellFormatterData {
	type: "ACTION";
	format: CellFormatterData;
	action: string;
	srcType: number;
	params?: ActionParam[];
}

export interface DefaultFormatterData extends CellFormatterData {
	type: "DEFAULT";
}

export interface TagFormatterData extends CellFormatterData {
	type: "TAG";
	format: CellFormatterData;
	scope?: string;
}

export type DurationUnit = "NANOSECONDS"|"MICROSECONDS"|"MILLISECONDS"|"SECONDS"|"MINUTES"|"HOURS"|"DAYS";

export interface DurationFormatterData extends CellFormatterData {
	type: "DURATION";
	unit?: DurationUnit;

}

export interface PackFormatterRule {
	type: "VALUE"|"TAKE"|"PROPERTY";
	value?: any;
	index?: number;
	property?: string;
	defaultValue?: any;
}

export interface PackFormatterData extends CellFormatterData {
	type: "PACK";
	format: CellFormatterData;
	rules: PackFormatterRule[];
}

export type FragmentEscape = "NO" | "TEXT" | "ATTR";

export interface FragmentData {
	esc: FragmentEscape;
	content: string;
}

export interface EnumFormatterData extends CellFormatterData {
	type: "ENUM";
	map: Record<string, FragmentData[]>;
}

export interface I18nFormatterData extends CellFormatterData {
	type: "I18N";
	html?: boolean;
}

export interface ExpandFormatterData extends CellFormatterData {
	type: "EXPAND";
	format: CellFormatterData;
}

export interface ConcatFormatterData extends CellFormatterData {
	type: "CONCAT";
	inputs: CellFormatterData[];
}

export interface LinkFormatterData extends CellFormatterData {
	type: "LINK";
	format: CellFormatterData;
	internal?: boolean;
}

export interface WrapFormatterData extends CellFormatterData {
	type: "WRAP";
	format: CellFormatterData;
	tag: string;
	attributes?: Record<string, string>;
}

// ---
export type FormattingFunction = (ctx:CellContext, ...data:any) => SlotFactory;

export interface CellFormatter {
	format: FormattingFunction
}

function escapeHtmlText(text: string): string {
	let result = "";

	for (let i = 0; i < text.length; i++) {
		const ch = text.charCodeAt(i);

		// &, <, >
		if (ch === 38) result += "&amp;";
		else if (ch === 60) result += "&lt;";
		else if (ch === 62) result += "&gt;";
		else result += text[i];
	}

	return result;
}

function escapeHtmlAttr(text: string): string {
	let result = "";

	for (let i = 0; i < text.length; i++) {
		const ch = text.charCodeAt(i);

		// &, <, >, ", ', \n
		if (ch === 38) result += "&amp;";
		else if (ch === 60) result += "&lt;";
		else if (ch === 62) result += "&gt;";
		else if (ch === 34) result += "&quot;";
		else if (ch === 39) result += "&#39;";
		else if (ch === 10) result += "&#10;";
		else result += text[i];
	}

	return result;
}


export class Fragment {
	private content:string;
	private esc:FragmentEscape;

	constructor(content:string, esc: FragmentEscape = "TEXT") {
		this.content = content;
		this.esc = esc;
	}

	static ofHtml(content:string) {
		return new Fragment(content, "NO");
	}

	static ofAttr(content:string) {
		return new Fragment(content, "ATTR");
	}

	static of(content:string, safeHtml = false) {
		return new Fragment(content, safeHtml ? "NO" : "TEXT");
	}

	static empty() {
		return EMPTY_FRAGMENT;
	}
}
const EMPTY_FRAGMENT = new Fragment("", "NO");


// TODO implement limiting functions
export function limitHtml(txt:string, limit: number):string {
	return txt;
}

export function limitText(txt:string, limit: number):string {
	return txt;
}
